import { fitTables } from "./tableFit";

/**
 * Verhindert Neu-Laden/Neu-Zeichnen von Diagrammen und Bildern bei jedem
 * Tastendruck.
 *
 * `preview.innerHTML = renderMarkdown(...)` baut bei jeder Änderung die
 * komplette Vorschau neu auf – auch Knoten, deren Quelltext sich gar nicht
 * geändert hat. Das SVG-Cache in tikz.ts/mermaid.ts verhindert zwar erneute
 * Netzwerk-Requests, aber der DOM-Knoten selbst wird trotzdem frisch erzeugt
 * und muss neu eingefügt/gemalt werden – das ist das sichtbare Flackern.
 *
 * Fix: bevor der neue HTML-String eingesetzt wird, „adoptiert" diese Funktion
 * unveränderte Diagramm-/Bild-Knoten aus dem ALTEN DOM in den NEUEN String –
 * per Quelltext-Hash identifiziert. Bereits geladene/gemalte Elemente bleiben
 * so exakt derselbe Knoten, nur wirklich geänderte werden neu gebaut.
 */

function restoreTableScroll(roots: Element[], scrollLefts: number[]): void {
  within(roots, ".md-table-scroll").forEach((el, i) => {
    const left = scrollLefts[i];
    if (left) (el as HTMLElement).scrollLeft = left;
  });
}

function hashText(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/**
 * Eindeutiger Schlüssel für einen wiederverwendbaren Knoten – jeweils aus
 * einem Attribut, das den Quelltext identifiziert (nicht geraten).
 */
function keyOf(el: Element): string | null {
  if (el.classList.contains("md-tikz")) {
    return "tikz:" + (el.getAttribute("data-kroki-type") ?? "") + ":" + (el.getAttribute("data-tikz") ?? "");
  }
  if (el.classList.contains("md-mermaid") || el.classList.contains("md-diagram-error")) {
    const hash = el.getAttribute("data-src-hash");
    return hash ? "mmd:" + hash : null;
  }
  if (el.tagName === "PRE" && el.hasAttribute("data-src-hash")) {
    // Noch nicht hydrierter Mermaid-Fence (vor dem async Render).
    return "mmd:" + el.getAttribute("data-src-hash");
  }
  if (el.tagName === "IMG") {
    return "img:" + (el.getAttribute("src") ?? "");
  }
  if (el.tagName === "CODE" && el.parentElement?.tagName === "PRE" && el.hasAttribute("data-hl")) {
    return "code:" + hashText(el.textContent ?? "") + ":" + el.getAttribute("data-hl");
  }
  return null;
}

/**
 * Ersetzt in `container` (mit dem NEUEN `html` befüllt) alle Knoten, die im
 * ALTEN Zustand bereits identisch und fertig gerendert vorlagen, durch die
 * Original-Referenzen. Bilder werden nur übernommen, wenn sie schon geladen
 * waren (kein erneutes Laden/Flackern beim Einfügen).
 */
export function updatePreservingRenderedNodes(container: HTMLElement, html: string): void {
  blockState.delete(container);
  patch(container, Array.from(container.childNodes), [{ html, anchor: null }]);
}

/**
 * Entfernt `oldNodes` (direkte Kinder von `container`) und setzt jedes Stück
 * neues `html` vor seinen `anchor` (`null` = ans Ende). Was davon schon fertig
 * gerendert vorlag (Diagramme, geladene Bilder, gefärbter Code), wird
 * übernommen statt neu gebaut.
 */
function patch(container: HTMLElement, oldNodes: Node[], inserts: { html: string; anchor: Node | null }[]): void {
  const oldRoots = oldNodes.filter((n): n is Element => n instanceof Element);
  const SELECTOR = ".md-tikz, .md-mermaid, .md-diagram-error, pre[data-src-hash], img, code[data-hl]";
  const before = new Map<string, Element>();
  for (const el of within(oldRoots, SELECTOR)) {
    if (el.tagName === "IMG" && !(el as HTMLImageElement).complete) continue;
    if (el.classList.contains("md-tikz") && el.classList.contains("is-loading")) continue;
    const key = keyOf(el);
    if (key && !before.has(key)) before.set(key, el);
  }

  // Horizontal gescrollte Tabellen sonst per Tastendruck zurück auf Anfang –
  // nach Reihenfolge statt Inhalt zugeordnet: bleibt auch richtig, wenn genau
  // die gerade betrachtete Tabelle mitbearbeitet wird.
  const tableScrollLefts = within(oldRoots, ".md-table-scroll").map((el) => (el as HTMLElement).scrollLeft);
  const oldDiagrams = topLevelDiagrams(oldRoots).map((el) => {
    const picture = pictureOf(el);
    return { picture, height: (picture ?? el).offsetHeight };
  });

  for (const n of oldNodes) n.parentNode?.removeChild(n);
  const newRoots: Element[] = [];
  const tpl = document.createElement("template");
  for (const { html, anchor } of inserts) {
    tpl.innerHTML = html;
    for (const n of tpl.content.childNodes) if (n instanceof Element) newRoots.push(n);
    container.insertBefore(tpl.content, anchor);
  }

  restoreTableScroll(newRoots, tableScrollLefts);
  if (before.size) adoptRendered(newRoots, before, SELECTOR);
  keepOldDiagramsUntilRendered(newRoots, oldDiagrams);
  fitTables(newRoots);
}

/** Alle Treffer in den Wurzeln selbst und darunter, in Dokumentreihenfolge. */
function within(roots: Element[], selector: string): Element[] {
  const out: Element[] = [];
  for (const root of roots) {
    if (root.matches(selector)) out.push(root);
    out.push(...root.querySelectorAll(selector));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Blockweises Aktualisieren (Vorschau beim Tippen)
// ---------------------------------------------------------------------------

/**
 * Jeder Block der obersten Ebene beginnt im DOM mit diesem Kommentar. CSS
 * (`h2 + p`, `:first-child`), `children` und `nextElementSibling` sehen
 * Kommentare nicht – Layout, Falten und Mitscrollen bleiben also unberührt.
 * Ersetzt das Nachzeichnen einen Knoten (Mermaid: `<pre>` -> `<div>`) oder
 * schiebt ein altes Diagramm davor, bleibt das trotzdem im richtigen Abschnitt.
 */
const MARK = "mdb";
const MARK_HTML = `<!--${MARK}-->`;

/** HTML je Block, so wie es zuletzt eingesetzt wurde. */
const blockState = new WeakMap<HTMLElement, string[]>();

const LINE_ATTRS = / data-line(?:-end)?="\d+"/g;

/** Vergleichsschlüssel: Zeilennummern verschieben sich beim Tippen davor, der Inhalt nicht. */
function blockKey(html: string): string {
  return html.replace(LINE_ATTRS, "");
}

function firstLine(html: string): number | null {
  const m = / data-line="(\d+)"/.exec(html);
  return m ? Number(m[1]) : null;
}

/** Direkte Kinder je Block; `null`, wenn der Inhalt nicht von `updateBlocks` stammt. */
function segments(container: HTMLElement): Node[][] | null {
  const segs: Node[][] = [];
  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType === Node.COMMENT_NODE && (node as Comment).data === MARK) segs.push([node]);
    else if (segs.length) segs[segs.length - 1].push(node);
    else return null;
  }
  return segs;
}

/** Unveränderter Block, nur weiter oben/unten im Quelltext: Anker mitverschieben. */
function shiftLines(seg: Node[], oldHtml: string, newHtml: string): void {
  if (oldHtml === newHtml) return;
  const a = firstLine(oldHtml);
  const b = firstLine(newHtml);
  if (a == null || b == null || a === b) return;
  const delta = b - a;
  for (const el of within(seg.filter((n): n is Element => n instanceof Element), "[data-line], [data-line-end]")) {
    for (const attr of ["data-line", "data-line-end"]) {
      const v = el.getAttribute(attr);
      if (v != null) el.setAttribute(attr, String(Number(v) + delta));
    }
  }
}

/**
 * Setzt die Blöcke aus `renderMarkdownBlocks` ein und fasst dabei nur an, was
 * sich geändert hat: ein Block mit gleichem Inhalt bleibt exakt derselbe Knoten
 * (kein neues Layout, kein Flackern, offene Aufklapper bleiben offen).
 *
 * Zuordnung in Reihenfolge statt nur über gemeinsamen Anfang/gemeinsames Ende:
 * ein Inhaltsverzeichnis oben ändert sich mit jeder Überschrift, und alles
 * zwischen ihm und der Tippstelle neu zu bauen wäre wieder fast die ganze Seite.
 */
export function updateBlocks(container: HTMLElement, blocks: string[]): void {
  const prev = blockState.get(container);
  const segs = prev ? segments(container) : null;
  if (!prev || !segs || segs.length !== prev.length) {
    patch(container, Array.from(container.childNodes), [{ html: blocks.map((b) => MARK_HTML + b).join(""), anchor: null }]);
    blockState.set(container, blocks);
    return;
  }

  // ponytail: gierige Zuordnung in Reihenfolge statt echter LCS – ein neuer
  // Block, der zufällig einem viel späteren gleicht, lässt die Blöcke
  // dazwischen neu bauen. Kommt beim Tippen praktisch nicht vor.
  const byKey = new Map<string, number[]>();
  prev.forEach((html, i) => {
    const key = keyCache.get(html) ?? blockKey(html);
    const list = byKey.get(key);
    if (list) list.push(i);
    else byKey.set(key, [i]);
  });
  const match = new Array<number>(blocks.length).fill(-1);
  let last = -1;
  const nextKeys = new Map<string, string>();
  blocks.forEach((html, j) => {
    const key = keyCache.get(html) ?? blockKey(html);
    nextKeys.set(html, key);
    const list = byKey.get(key);
    while (list?.length && list[0] <= last) list.shift();
    if (list?.length) last = match[j] = list.shift()!;
  });

  const kept = new Set(match.filter((i) => i >= 0));
  const removed: Node[] = [];
  segs.forEach((seg, i) => {
    if (!kept.has(i)) removed.push(...seg);
  });

  // Neue Blöcke zu Läufen bündeln, jeder Lauf landet vor dem nächsten behaltenen Block.
  const inserts: { html: string; anchor: Node | null }[] = [];
  let run = "";
  blocks.forEach((html, j) => {
    const i = match[j];
    if (i < 0) {
      run += MARK_HTML + html;
      return;
    }
    shiftLines(segs[i], prev[i], html);
    if (run) inserts.push({ html: run, anchor: segs[i][0] });
    run = "";
  });
  if (run) inserts.push({ html: run, anchor: null });

  if (removed.length || inserts.length) patch(container, removed, inserts);
  blockState.set(container, blocks);
  keyCache = nextKeys;
}

/** Schlüssel der zuletzt eingesetzten Blöcke – spart beim nächsten Tastendruck das Neuberechnen. */
let keyCache = new Map<string, string>();

const DIAGRAMS = ".md-tikz, .md-mermaid, .md-diagram-error, .md-diagram-hold, pre[data-src-hash]";
const PENDING = "pre[data-src-hash], .md-tikz.is-loading";

function topLevelDiagrams(roots: Element[]): HTMLElement[] {
  return (within(roots, DIAGRAMS) as HTMLElement[]).filter(
    (el) => !el.classList.contains("md-stale") && !el.parentElement?.closest(DIAGRAMS)
  );
}

function pictureOf(el: HTMLElement): HTMLElement | null {
  if (el.classList.contains("md-diagram-hold")) return el.querySelector<HTMLElement>(":scope > .md-stale");
  if (el.matches(PENDING)) return staleOf(el);
  if (el.querySelector("svg")) return el;
  return null;
}

// Beim Neu-Rendern bleibt das alte fertige Diagramm stehen, bis das neue fertig ist
// (dropStale/staleOf in mermaid.ts/tikz.ts) – sonst ist die Fläche kurz leer und die Scrollposition springt.
function keepOldDiagramsUntilRendered(roots: Element[], old: { picture: HTMLElement | null; height: number }[]): void {
  if (!old.length) return;
  const now = topLevelDiagrams(roots);
  if (now.length !== old.length) return;
  now.forEach((el, i) => {
    const { picture, height } = old[i];
    if (el.matches(PENDING) && picture && !picture.isConnected) {
      for (const attr of ["data-line", "data-line-end"]) {
        const value = el.getAttribute(attr);
        if (value != null) picture.setAttribute(attr, value);
        else picture.removeAttribute(attr);
      }
      picture.classList.add("md-stale");
      el.before(picture);
      el.classList.add("md-pending");
    } else if (el.offsetHeight < height) {
      el.style.minHeight = `${height}px`;
    }
  });
}

export function staleOf(placeholder: Element): HTMLElement | null {
  const prev = placeholder.previousElementSibling;
  return prev?.classList.contains("md-stale") ? (prev as HTMLElement) : null;
}

export function dropStale(placeholder: Element): void {
  staleOf(placeholder)?.remove();
  placeholder.classList.remove("md-pending");
}

function adoptRendered(roots: Element[], before: Map<string, Element>, SELECTOR: string): void {
  for (const el of within(roots, SELECTOR)) {
    const key = keyOf(el);
    if (!key) continue;
    const old = before.get(key);
    if (!old || old === el) continue;
    // Der Quelltext ist gleich (deshalb wiederverwendet) – die Zeilennummern
    // können sich trotzdem verschoben haben (Zeilen davor eingefügt/gelöscht).
    // Ohne Übernahme bekäme der Mitscroll-Abgleich (syncScroll.ts) hier einen
    // veralteten Anker, obwohl der Knoten selbst unverändert bleibt.
    for (const attr of ["data-line", "data-line-end"]) {
      const value = el.getAttribute(attr);
      if (value != null) old.setAttribute(attr, value);
      else old.removeAttribute(attr);
    }
    old.classList.remove("md-stale");
    el.replaceWith(old);
  }
}
